import makeStyles from '@material-ui/core/styles/makeStyles';
import { alpha } from '@material-ui/core/styles/colorManipulator';
import { useTranslation } from 'react-i18next';

import Avatar, { AvatarProps } from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Skeleton from '@material-ui/lab/Skeleton';

import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';

import * as utils from './utils';

const MAX_PROFILE_FILE_SIZE = 0.1;

const useStyles = makeStyles((theme) => ({
    rootBig: {
        position: 'relative',
        width: theme.spacing(35),
        height: theme.spacing(35),
    },

    root: {
        position: 'relative',
        width: theme.spacing(15),
        height: theme.spacing(15),

        '&__img': {
            width: 'inherit',
            height: 'inherit',
        },

        '&__input': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            zIndex: 1,
            opacity: 0,
            cursor: 'pointer',
            '&:hover + $root': {
                '&__overlay': {
                    opacity: 1,
                },
            },
        },

        '&__overlay': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha('#000', 0.6),
            opacity: 0,
        },

        '&__overlayIcon': {
            color: '#fff',
            fontSize: theme.typography.pxToRem(35),
        },
    },

}));

type AvatarInputProps = {
    isLoading?: boolean;
    onChange?: (base64Image: string) => unknown;
    AvatarProps?: AvatarProps;
    allowInput?: boolean;
    minimize?: boolean;
};

const AvatarInput: React.FC<AvatarInputProps> = (props) => {
    const { AvatarProps, isLoading, allowInput = true, minimize = true } = props;
    const classes = useStyles();
    const { t } = useTranslation();

    async function onChange(event: React.ChangeEvent<HTMLInputElement>) {
        const files = event.target.files;
        if (!files?.length) return;

        const file = files[0];
        let base64Image: string = URL.createObjectURL(file);

        const profile = await utils.resizeImage(file, { width: 120, height: 120 });
        base64Image = await utils.blobToBase64(profile);

        if (base64Image && utils.getBase64MbSize(base64Image) > MAX_PROFILE_FILE_SIZE) {
            throw new Error(t('general.components.avatarInput.errorFileSize'));
        }

        if (props.onChange) {
            props.onChange(base64Image);
        }
    }

    if (isLoading) {
        return (
            <Box className={classes.root}>
                <Skeleton variant="circle" className={`${classes.root}__img`} />
            </Box>
        );
    }

    return (
        <Box className={minimize ? classes.root : classes.rootBig}>
            <Avatar className={`${classes.root}__img`} {...AvatarProps} />

            {allowInput && (
                <>
                    <input title="" type="file" accept="image/*" className={`${classes.root}__input`} onChange={onChange} />
                    <Box className={`${classes.root}__overlay`}>
                        <PhotoCameraIcon className={`${classes.root}__overlayIcon`} />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AvatarInput;
